import type { ComponentPropsWithoutRef, HTMLAttributes, ReactNode } from "react";
import { Link, type To } from "react-router-dom";
import { Icon } from "./Icon";
import "./customer-layout.css";

function classNames(...values: Array<string | false | undefined>) {
  return values.filter(Boolean).join(" ");
}

export type CustomerPageCanvasProps = ComponentPropsWithoutRef<"main">;

/**
 * The LC-93 customer page root. It establishes the design-token scope and the
 * skip-link target without prescribing a route's sections or data states.
 */
export function CustomerPageCanvas({
  id = "main-content",
  className,
  children,
  ...props
}: CustomerPageCanvasProps) {
  return (
    <main
      {...props}
      id={id}
      className={classNames("customer-design-system", "lc-page-canvas", className)}
    >
      {children}
    </main>
  );
}

export type CustomerContentRailWidth = "reading" | "standard" | "wide" | "full";

export type CustomerContentRailProps = ComponentPropsWithoutRef<"div"> & {
  width?: CustomerContentRailWidth;
};

/** Keeps content centered and legible while its surrounding band remains full bleed. */
export function CustomerContentRail({
  width = "standard",
  className,
  children,
  ...props
}: CustomerContentRailProps) {
  return (
    <div
      {...props}
      className={classNames("lc-content-rail", `lc-content-rail--${width}`, className)}
    >
      {children}
    </div>
  );
}

export type CustomerSectionBandTone = "canvas" | "surface" | "subtle" | "ink" | "transparent";
export type CustomerSectionBandSpacing = "none" | "compact" | "standard" | "generous";

export type CustomerSectionBandProps = HTMLAttributes<HTMLElement> & {
  as?: "section" | "div";
  tone?: CustomerSectionBandTone;
  spacing?: CustomerSectionBandSpacing;
};

/** A full-width, borderless page section. Use `as="div"` when it is not a named landmark. */
export function CustomerSectionBand({
  as: Element = "section",
  tone = "canvas",
  spacing = "standard",
  className,
  children,
  ...props
}: CustomerSectionBandProps) {
  return (
    <Element
      {...props}
      className={classNames(
        "lc-section-band",
        `lc-section-band--${tone}`,
        `lc-section-band--${spacing}`,
        className,
      )}
    >
      {children}
    </Element>
  );
}

export type CustomerPageHeroProps = Omit<
  ComponentPropsWithoutRef<"header">,
  "children" | "title"
> & {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  visual?: ReactNode;
  align?: "start" | "center";
  headingLevel?: 1 | 2;
  headingId?: string;
};

/**
 * Shared hero hierarchy only. Pair it with CustomerSectionBand and
 * CustomerContentRail so each route can choose its surface and measure.
 */
export function CustomerPageHero({
  eyebrow,
  title,
  description,
  actions,
  visual,
  align = "start",
  headingLevel = 1,
  headingId,
  className,
  ...props
}: CustomerPageHeroProps) {
  const Heading = headingLevel === 1 ? "h1" : "h2";

  return (
    <header
      {...props}
      className={classNames(
        "lc-page-hero",
        `lc-page-hero--${align}`,
        Boolean(visual) && "lc-page-hero--with-visual",
        className,
      )}
    >
      <div className="lc-page-hero__copy">
        {eyebrow && <p className="lc-page-hero__eyebrow">{eyebrow}</p>}
        <Heading id={headingId}>{title}</Heading>
        {description && (
          <div className="lc-page-hero__description">
            {typeof description === "string" ? <p>{description}</p> : description}
          </div>
        )}
        {actions && <div className="lc-page-hero__actions">{actions}</div>}
      </div>
      {visual && <div className="lc-page-hero__visual">{visual}</div>}
    </header>
  );
}

export type CustomerBreadcrumbItem = Readonly<{
  label: ReactNode;
  to?: To;
}>;

export type CustomerBreadcrumbProps = Omit<
  ComponentPropsWithoutRef<"nav">,
  "aria-label" | "children"
> & {
  items: readonly CustomerBreadcrumbItem[];
  label?: string;
};

/** The final item is always rendered as the current page, never as a redundant link. */
export function CustomerBreadcrumb({
  items,
  label = "Breadcrumb",
  className,
  ...props
}: CustomerBreadcrumbProps) {
  if (items.length === 0) return null;

  return (
    <nav {...props} className={classNames("lc-breadcrumb", className)} aria-label={label}>
      <ol>
        {items.map((item, index) => {
          const current = index === items.length - 1;
          const key = typeof item.label === "string" ? `${item.label}-${index}` : index;
          return (
            <li key={key}>
              {!current && item.to !== undefined ? (
                <Link to={item.to}>{item.label}</Link>
              ) : (
                <span aria-current={current ? "page" : undefined}>{item.label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

type CustomerBookIdentityChipBaseProps = {
  title: ReactNode;
  meta?: ReactNode;
  visual?: ReactNode;
  className?: string;
};

type CustomerBookIdentityChipLinkProps = CustomerBookIdentityChipBaseProps &
  Omit<ComponentPropsWithoutRef<typeof Link>, "children" | "className" | "title" | "to"> & {
    to: To;
  };

type CustomerBookIdentityChipStaticProps = CustomerBookIdentityChipBaseProps &
  Omit<ComponentPropsWithoutRef<"div">, "children" | "className" | "title"> & {
    to?: undefined;
  };

export type CustomerBookIdentityChipProps =
  | CustomerBookIdentityChipLinkProps
  | CustomerBookIdentityChipStaticProps;

function BookIdentityContent({ title, meta, visual }: CustomerBookIdentityChipBaseProps) {
  return (
    <>
      <span className="lc-book-identity-chip__visual" aria-hidden="true">
        {visual ?? <Icon name="book" size={20} />}
      </span>
      <span className="lc-book-identity-chip__copy">
        <strong>{title}</strong>
        {meta && <span>{meta}</span>}
      </span>
    </>
  );
}

/** A compact book context marker for customer headers and workflow handoffs. */
export function CustomerBookIdentityChip(props: CustomerBookIdentityChipProps) {
  if (props.to !== undefined) {
    const { title, meta, visual, className, to, ...linkProps } = props;
    return (
      <Link {...linkProps} className={classNames("lc-book-identity-chip", className)} to={to}>
        <BookIdentityContent title={title} meta={meta} visual={visual} />
      </Link>
    );
  }

  const { title, meta, visual, className, ...staticProps } = props;
  return (
    <div {...staticProps} className={classNames("lc-book-identity-chip", className)}>
      <BookIdentityContent title={title} meta={meta} visual={visual} />
    </div>
  );
}
